import { useMemo, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft, Monitor, Smartphone, Tablet, Laptop,
  ExternalLink, RefreshCw, RotateCw, ChevronDown, Maximize2, Minimize2,
} from "lucide-react";
import { Button } from "../../components/ui/button";

/**
 * Preview categories:
 *  desktop → fluid widths (laptop / desktop / wide)
 *  tablet  → iPad presets
 *  phone   → iPhone / Pixel / Galaxy presets
 *  fold    → Samsung Galaxy Z Fold 8 (closed outer screen + opened inner screen)
 */

const DESKTOPS = [
  { key: "laptop",   label: "Laptop (1280)",     icon: Laptop,  width: 1280, height: null },
  { key: "desktop",  label: "Masaüstü (1440)",   icon: Monitor, width: 1440, height: null },
  { key: "wide",     label: "Geniş (1920)",      icon: Monitor, width: 1920, height: null },
  { key: "fluid",    label: "Esnek (ekranın tamamı)", icon: Monitor, width: null, height: null },
];

const TABLETS = [
  { key: "ipad-mini",   label: "iPad Mini",      width: 744,  height: 1133 },
  { key: "ipad",        label: 'iPad (10.9")',   width: 820,  height: 1180 },
  { key: "ipad-pro-11", label: 'iPad Pro 11"',   width: 834,  height: 1194 },
  { key: "ipad-pro-13", label: 'iPad Pro 13"',   width: 1024, height: 1366 },
  { key: "surface",     label: "Surface Pro",    width: 912,  height: 1368 },
];

const PHONES = [
  { key: "iphone-se",       label: "iPhone SE",             width: 375, height: 667  },
  { key: "iphone-14",       label: "iPhone 14",             width: 390, height: 844  },
  { key: "iphone-15-pro",   label: "iPhone 15 Pro",         width: 393, height: 852  },
  { key: "iphone-15-pro-max", label: "iPhone 15 Pro Max",   width: 430, height: 932  },
  { key: "pixel-7",         label: "Pixel 7",               width: 412, height: 915  },
  { key: "pixel-8-pro",     label: "Pixel 8 Pro",           width: 448, height: 998  },
  { key: "galaxy-s23",      label: "Galaxy S23",            width: 360, height: 780  },
  { key: "galaxy-s24-ultra",label: "Galaxy S24 Ultra",      width: 384, height: 832  },
];

const FOLDS = [
  { key: "z-fold-8-closed", label: "Z Fold 8 (katlı)",   width: 374, height: 993,  variant: "closed" },
  { key: "z-fold-8-open",   label: "Z Fold 8 (açık)",    width: 884, height: 1104, variant: "open"   },
  { key: "z-flip-6",        label: "Z Flip 6",           width: 375, height: 879,  variant: "flip"   },
];

const ALL = [...DESKTOPS, ...TABLETS, ...PHONES, ...FOLDS];

function pickById(id) { return ALL.find((d) => d.key === id) || DESKTOPS[1]; }

export default function ServicePreview() {
  const params = useParams();
  const loc = useLocation();
  const search = new URLSearchParams(loc.search);
  const customPath = search.get("path");
  const slug = params.slug;
  const previewPath = customPath || (slug ? `/calismalarim/${slug}` : "/");
  const backTo = search.get("back") || (slug ? "/admin/calismalarim" : "/admin");
  const title = search.get("title") || (slug ? slug : "Site önizleme");

  const [deviceId, setDeviceIdRaw] = useState(() => {
    const q = search.get("device");
    return (q && ALL.find((d) => d.key === q)) ? q : "desktop";
  });
  const [rotated, setRotated] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [fullBleed, setFullBleed] = useState(false);
  const setDeviceId = (id) => { setDeviceIdRaw(id); setRotated(false); };

  const device = useMemo(() => pickById(deviceId), [deviceId]);
  const url = `${previewPath}${previewPath.includes("?") ? "&" : "?"}preview=1&t=${nonce}`;

  const category = DESKTOPS.includes(device)
    ? "desktop"
    : TABLETS.includes(device)
      ? "tablet"
      : FOLDS.includes(device)
        ? "fold"
        : "phone";

  const isFluid = device.width == null;
  const hasFixedHeight = device.height != null;
  const w = isFluid ? null : (rotated && hasFixedHeight ? device.height : device.width);
  const h = isFluid ? null : (rotated && hasFixedHeight ? device.width : device.height);
  const canRotate = !isFluid && hasFixedHeight;

  const frameClass = (() => {
    if (isFluid) return "";
    if (category === "phone") return "rounded-[42px] ring-[12px] ring-stone-900 shadow-2xl";
    if (category === "tablet") return "rounded-[22px] ring-[10px] ring-stone-700 shadow-xl";
    if (category === "fold") {
      return device.variant === "open"
        ? "rounded-[18px] ring-[8px] ring-stone-800 shadow-xl relative"
        : "rounded-[32px] ring-[10px] ring-stone-900 shadow-2xl";
    }
    return "rounded-[12px] ring-1 ring-stone-300 shadow-lg";
  })();

  return (
    <div className="min-h-screen bg-stone-100 flex flex-col" data-testid="service-preview-page">
      {/* Toolbar */}
      <header className="sticky top-0 z-20 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900" data-testid="preview-back">
              <ArrowLeft size={14} />Düzenleyiciye dön
            </Link>
            <span className="text-stone-300">·</span>
            <p className="text-sm truncate">
              <span className="text-stone-400">Önizleme:</span>{" "}
              <span className="font-mono text-stone-700">{previewPath}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Category tabs */}
            <div className="inline-flex items-center bg-stone-100 rounded-full p-1" role="tablist">
              {[
                { k: "desktop", label: "Masaüstü", Icon: Monitor,  first: DESKTOPS[1] },
                { k: "tablet",  label: "Tablet",   Icon: Tablet,   first: TABLETS[1]  },
                { k: "phone",   label: "Mobil",    Icon: Smartphone, first: PHONES[2] },
                { k: "fold",    label: "Katlanır", Icon: Smartphone, first: FOLDS[0]  },
              ].map((c) => {
                const active = category === c.k;
                return (
                  <button
                    key={c.k}
                    type="button"
                    onClick={() => setDeviceId(c.first.key)}
                    className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1.5 transition-all ${active ? "bg-white shadow text-stone-900" : "text-stone-500 hover:text-stone-800"}`}
                    data-testid={`preview-category-${c.k}`}
                    aria-pressed={active}
                  >
                    <c.Icon size={14} />{c.label}
                  </button>
                );
              })}
            </div>

            <DeviceDropdown
              category={category}
              current={device}
              onChange={(d) => setDeviceId(d.key)}
            />

            {canRotate && (
              <Button type="button" variant="outline" size="sm" onClick={() => setRotated((v) => !v)} data-testid="preview-rotate" title="Döndür">
                <RotateCw size={14} className="mr-1" />{rotated ? "Dikey" : "Yatay"}
              </Button>
            )}

            <Button type="button" variant="outline" size="sm" onClick={() => setFullBleed((v) => !v)} data-testid="preview-fullbleed" title={fullBleed ? "Çerçeveli göster" : "Çerçevesiz göster"}>
              {fullBleed ? <Minimize2 size={14} className="mr-1" /> : <Maximize2 size={14} className="mr-1" />}
              {fullBleed ? "Çerçeveli" : "Çerçevesiz"}
            </Button>

            <Button type="button" variant="outline" size="sm" onClick={() => setNonce((n) => n + 1)} data-testid="preview-refresh">
              <RefreshCw size={14} className="mr-1" />Yenile
            </Button>
            <a href={url} target="_blank" rel="noopener noreferrer">
              <Button type="button" variant="outline" size="sm" data-testid="preview-open-tab">
                <ExternalLink size={14} className="mr-1" />Yeni sekme
              </Button>
            </a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 pb-3 text-[11px] text-stone-500 flex items-center gap-3 flex-wrap">
          <span><strong className="text-stone-700">{device.label}</strong></span>
          {!isFluid && hasFixedHeight && (
            <span className="font-mono bg-stone-100 rounded px-1.5 py-0.5">
              {w}×{h}px{rotated ? " (yatay)" : ""}
            </span>
          )}
          {!isFluid && !hasFixedHeight && (
            <span className="font-mono bg-stone-100 rounded px-1.5 py-0.5">
              {w}px (esnek yükseklik)
            </span>
          )}
          {category === "fold" && device.variant === "open" && (
            <span className="text-amber-700 bg-amber-50 border border-amber-200/70 rounded-full px-2 py-0.5">Katlanmış ekran açık — tablet moduna geçer</span>
          )}
          <span className="truncate">· <span className="font-mono">{title}</span></span>
        </div>
      </header>

      {/* Preview frame */}
      <main className="flex-1 flex items-start justify-center overflow-auto py-6 px-4">
        <div
          className={`relative shrink-0 bg-white transition-all duration-300 overflow-hidden ${fullBleed ? "" : frameClass}`}
          style={{
            width: isFluid ? "100%" : `${w}px`,
            maxWidth: isFluid ? "100%" : `${w}px`,
            height: (isFluid || !hasFixedHeight) ? "min(1200px, calc(100vh - 160px))" : `${h}px`,
          }}
          data-testid="preview-stage"
        >
          {!fullBleed && category === "phone" && (
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bg-stone-900 w-28 h-5 rounded-b-2xl z-10" aria-hidden />
          )}
          {!fullBleed && category === "fold" && device.variant === "open" && (
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-stone-200 pointer-events-none z-10" aria-hidden />
          )}
          <iframe
            key={nonce}
            src={url}
            title={`Önizleme — ${title}`}
            className="w-full h-full block"
            style={{ border: 0 }}
            data-testid="preview-iframe"
          />
        </div>
      </main>
    </div>
  );
}

function DeviceDropdown({ category, current, onChange }) {
  const [open, setOpen] = useState(false);
  const list = category === "desktop" ? DESKTOPS
    : category === "tablet" ? TABLETS
    : category === "fold" ? FOLDS
    : PHONES;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 border rounded-full bg-white px-3 py-1.5 text-xs sm:text-sm font-medium hover:border-stone-400"
        data-testid="preview-device-dropdown"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate max-w-[160px]">{current.label}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <button type="button" className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} aria-label="kapat" />
          <ul role="listbox" className="absolute right-0 mt-1 z-20 min-w-[220px] bg-white border rounded-lg shadow-lg p-1 max-h-80 overflow-auto" data-testid="preview-device-list">
            {list.map((d) => (
              <li key={d.key}>
                <button
                  type="button"
                  onClick={() => { onChange(d); setOpen(false); }}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md text-left text-sm hover:bg-stone-50 ${current.key === d.key ? "bg-stone-100 text-stone-900 font-medium" : "text-stone-700"}`}
                  data-testid={`preview-device-opt-${d.key}`}
                  role="option"
                  aria-selected={current.key === d.key}
                >
                  <span className="truncate">{d.label}</span>
                  <span className="text-[10px] font-mono text-stone-400 shrink-0">
                    {d.width ? `${d.width}×${d.height}` : "esnek"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
